// Global
import {
  Field,
  GetStaticComponentProps,
  Text,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import graphQLClientFactory from 'lib/graphql-client-factory';
import React, { useEffect, useState } from 'react';

// Local
import { BreadcrumbDataType } from './Breadcrumb.types';
import BreadcrumbQuery from './Breadcrumb.graphql';
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import { tailwindVariants } from './BreadcrumbTailwind';
import { useTheme } from 'lib/context/ThemeContext';
import { IngredientInfoPath } from '../HeroIngredient/HeroIngredient';

export type BreadcrumbDataProps = BreadcrumbDataType & { ingredientData: Ingredient };

type Ingredient = {
  item: {
    title: {
      value: string;
    };
  };
};

export const Breadcrumb = (staticProps: BreadcrumbDataType): JSX.Element => {
  const { ancestors, breadcrumbTitle } = staticProps?.staticProps?.currentPage || {};

  const { componentName, dataSource } = staticProps?.rendering || {};
  const {
    base,
    currentPageContainer,
    currentPageText,
    link,
    linkIcon,
    linkContainer,
    wrapper,
    innerWrapper,
    componentBG,
    gradient,
  } = tailwindVariants();

  const { sitecoreContext } = useSitecoreContext();
  const [themeBg, setThemeBg] = useState({});
  const { themeName } = useTheme();

  useEffect(() => {
    switch (themeName) {
      case 'Vanilla':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #191b1c 104.17%)',
        });
        break;
      case 'Pledge':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #eff7fd 104.17%)',
        });
        break;
      case 'Windex':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #ebf6ff 104.17%)',
        });
        break;
      default:
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #fff 104.17%)',
        });
        break;
    }
  }, [themeName]);

  // Check if the product is registered or not
  const isRegisteredProduct = (sitecoreContext?.route?.fields?.isRegistered as Field<boolean>)
    ?.value;

  // If the product is registered, we do not want to show the breadcrumb
  if (isRegisteredProduct) {
    return <></>;
  }

  return (
    <div className={componentBG()}>
      <Container>
        <div data-component="authorable/landmarks/breadcrumbs" data-testid="breadcrumbs">
          <div className={base()}>
            <nav className={wrapper()} aria-label="Breadcrumb">
              <ol className={innerWrapper()}>
                {ancestors
                  ?.slice()
                  .reverse()
                  .map((itm, index: React.Key | null | undefined) => {
                    let hideBreadcrumb = false;

                    itm?.disabledLinkNames?.names?.map((disitm) => {
                      if (disitm?.field?.disabled?.value === 'breadcrumb') hideBreadcrumb = true;
                    });

                    if (hideBreadcrumb) return;

                    return (
                      itm?.breadcrumbTitle?.jsonValue?.value && (
                        <li className={linkContainer()} key={index}>
                          <LinkWrapper
                            className={link()}
                            field={{
                              value: {
                                href: itm?.pageUrl?.link,
                                text: itm?.breadcrumbTitle?.jsonValue?.value,
                                title: itm?.breadcrumbTitle?.jsonValue?.value,
                              },
                            }}
                            gtmEvent={{
                              event: 'link',
                              type: 'breadcrumb',
                              'gtm.element.dataset.gtmDatasourceId': dataSource,
                              'gtm.element.dataset.gtmComponentName': componentName,
                            }}
                          />
                          <GoogleMaterialSymbol
                            className={linkIcon()}
                            icon="chevron_right"
                            aria-hidden={true}
                          />
                        </li>
                      )
                    );
                  })}
                <li className={currentPageContainer()}>
                  <Text
                    className={currentPageText()}
                    encode={false}
                    field={{
                      value: breadcrumbTitle?.jsonValue?.value,
                    }}
                    tag="span"
                  />
                </li>
              </ol>
            </nav>
            <div className={gradient()} style={themeBg}></div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export const WildCard = (props: BreadcrumbDataProps): JSX.Element => {
  const { ancestors, breadcrumbTitle } = props?.staticProps?.currentPage || {};
  const { ingredientData } = props || {};

  const { componentName, dataSource } = props?.rendering || {};
  const {
    base,
    currentPageContainer,
    currentPageText,
    link,
    linkIcon,
    linkContainer,
    wrapper,
    innerWrapper,
    componentBG,
    gradient,
  } = tailwindVariants();

  const { sitecoreContext } = useSitecoreContext();
  const [themeBg, setThemeBg] = useState({});
  const { themeName } = useTheme();

  useEffect(() => {
    switch (themeName) {
      case 'Vanilla':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #191b1c 104.17%)',
        });
        break;
      case 'Pledge':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #eff7fd 104.17%)',
        });
        break;
      case 'Windex':
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #ebf6ff 104.17%)',
        });
        break;
      default:
        setThemeBg({
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -4.17%, #fff 104.17%)',
        });
        break;
    }
  }, [themeName]);

  // Check if the product is registered or not
  const isRegisteredProduct = (sitecoreContext?.route?.fields?.isRegistered as Field<boolean>)
    ?.value;

  // If the product is registered, we do not want to show the breadcrumb
  if (isRegisteredProduct) {
    return <></>;
  }

  return (
    <div className={componentBG()}>
      <Container>
        <div data-component="authorable/landmarks/breadcrumbs" data-testid="breadcrumbs">
          <div className={base()}>
            <nav className={wrapper()} aria-label="Breadcrumb">
              <ol className={innerWrapper()}>
                {ancestors
                  ?.slice()
                  .reverse()
                  .map((itm, index: React.Key | null | undefined) => {
                    let hideBreadcrumb = false;

                    itm?.disabledLinkNames?.names?.map((disitm) => {
                      if (disitm?.field?.disabled?.value === 'breadcrumb') hideBreadcrumb = true;
                    });

                    if (hideBreadcrumb) return;

                    return (
                      itm?.breadcrumbTitle?.jsonValue?.value && (
                        <li className={linkContainer()} key={index}>
                          <LinkWrapper
                            className={link()}
                            field={{
                              value: {
                                href: itm?.pageUrl?.link,
                                text: itm?.breadcrumbTitle?.jsonValue?.value,
                                title: itm?.breadcrumbTitle?.jsonValue?.value,
                              },
                            }}
                            gtmEvent={{
                              event: 'link',
                              type: 'breadcrumb',
                              'gtm.element.dataset.gtmDatasourceId': dataSource,
                              'gtm.element.dataset.gtmComponentName': componentName,
                            }}
                          />
                          <GoogleMaterialSymbol
                            className={linkIcon()}
                            icon="chevron_right"
                            aria-hidden={true}
                          />
                        </li>
                      )
                    );
                  })}
                {ingredientData?.item?.title?.value ? (
                  <li className={currentPageContainer()}>
                    <Text
                      className={currentPageText()}
                      encode={false}
                      field={{
                        value: ingredientData?.item?.title?.value,
                      }}
                      tag="span"
                    />
                  </li>
                ) : (
                  <li className={currentPageContainer()}>
                    <Text
                      className={currentPageText()}
                      encode={false}
                      field={{
                        value: breadcrumbTitle?.jsonValue?.value,
                      }}
                      tag="span"
                    />
                  </li>
                )}
              </ol>
            </nav>
            <div className={gradient()} style={themeBg}></div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Breadcrumb;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData, context) => {
  const { requestPath } = context?.params || {};
  const requestPathValue = requestPath?.[0];
  const basePathId =
    layoutData?.sitecore?.route?.fields?.ingredientBasePath &&
    'id' in layoutData.sitecore.route.fields.ingredientBasePath
      ? layoutData.sitecore.route.fields.ingredientBasePath.id
      : null;

  const siteName = layoutData?.sitecore?.context?.site?.name;
  const graphQLClient = graphQLClientFactory({});
  let data = null;
  if (siteName == 'WISCJ' && requestPathValue && basePathId) {
    const basePathItem = await graphQLClient.request<IngredientInfoPath>(ingredientBasePathQuery, {
      basePathId: basePathId,
      language: layoutData?.sitecore?.context?.language,
    });
    const basePath = basePathItem?.item?.path;

    let attempts = 0;
    let isValidItem = false;
    const itemName = requestPathValue;
    let folderPath = '';

    if (itemName) {
      const firstChar = itemName.charAt(0);
      folderPath = !isNaN(Number(firstChar)) ? '0' : firstChar;

      do {
        attempts++;
        const itemPath = basePath + '/' + folderPath + '/' + itemName;
        data = await graphQLClient.request<Ingredient>(query, {
          path: itemPath,
          params: rendering.params,
          language: layoutData?.sitecore?.context?.language,
        });
        isValidItem = !!data?.item;
        folderPath = 'Hashtag';
      } while (!isValidItem && attempts < 2);
    }
  }

  const result = await graphQLClient.request<unknown>(BreadcrumbQuery, {
    datasource: rendering.dataSource,
    params: rendering.params,
    language: layoutData?.sitecore?.context?.language,
    itemID: layoutData?.sitecore?.route?.itemId,
  });
  return {
    staticProps: result,
    ingredientData: data,
  };
};

const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      ... on Ingredient {
        title {
          value
        }
      }
    }
  }
`;

const ingredientBasePathQuery = /* GraphQL */ `
  query IngredientBasePathQuery($basePathId: String!, $language: String!) {
    item(path: $basePathId, language: $language) {
      path
    }
  }
`;
